/* begin general import */
import { cloneDeep } from "lodash";
import { AppUser, AppUserFilter } from "models/AppUser";
import { ClassShiftFilter } from "models/ClassShift";
import { CourseSectionFilter } from "models/CourseSection";
import { EducationTypeFilter } from "models/EducationType";
import { SemesterFilter } from "models/Semester";
import { StatusFilter } from "models/Status";
import { SubjectFilter } from "models/Subject";
import { WeekdayFilter } from "models/Weekday";
import { Moment } from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { DateFilter, IdFilter, StringFilter } from "react3l-advanced-filters";
import { AdvanceDateFilter, AdvanceIdFilter } from "react3l-ui-library";
import AdvanceStringFilter from "react3l-ui-library/build/components/AdvanceFilter/AdvanceStringFilter";
import Drawer, {
  DrawerProps,
} from "react3l-ui-library/build/components/Drawer/Drawer";
import { Reducer } from "redux";
import { courseSectionRepository } from "repositories/course-section-repository";
import {
  FilterAction,
  FilterActionEnum,
  filterReducer,
  filterService,
} from "services/page-services/filter-service";

/* end individual import */

export interface CourseSectionFilterProps extends DrawerProps {
  filter?: any;
  setVisible?: any;
  handleChangeAllFilter?: (data: any) => void;
}

function CourseSectionAdvanceFilter(props: CourseSectionFilterProps) {
  const [translate] = useTranslation();

  const { visible, filter, setVisible, handleChangeAllFilter, handleClose } =
    props;

  const [modelFilter, setModelFilter] = React.useReducer<
    Reducer<CourseSectionFilter, FilterAction<CourseSectionFilter>>
  >(filterReducer, filter);

  const { handleChangeInputFilter, handleChangeSelectFilter } =
    filterService.useFilter(filter, setModelFilter);

  const handleSaveModelFilter = React.useCallback(() => {
    handleChangeAllFilter(modelFilter);
    setVisible(false);
  }, [handleChangeAllFilter, modelFilter, setVisible]);

  const handleClearModelFilter = React.useCallback(() => {
    setModelFilter({
      type: 0,
      payload: new CourseSectionFilter(),
    });
  }, [setModelFilter]);

  const handleChangeDateFilter = React.useCallback((dateMoment: Moment, dateString) => {
    const newDataMoment = cloneDeep(dateMoment);
    const cloneFilter = cloneDeep(filter);
    const startDate = dateMoment?.startOf("day").millisecond(0o1);
    const endDate = newDataMoment?.endOf("day");
    cloneFilter.lessonDate =
      Object.assign(new DateFilter(), {
        greaterEqual: startDate,
        lessEqual: endDate,
      })

    setModelFilter({
      type: FilterActionEnum.SET,
      payload: cloneFilter
    })
  }, [filter]);

  return (
    <Drawer
      visible={visible}
      handleSave={handleSaveModelFilter}
      handleCancel={handleClearModelFilter}
      handleClose={handleClose}
      visibleFooter={true}
      loading={false}
      size={"sm"}
      title={translate("general.drawer.filter")}
      titleButtonCancel={translate("general.actions.clear")}
      titleButtonApply={translate("general.actions.apply")}
    >
      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("courseSections.code")}
          maxLength={100}
          value={modelFilter["code"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "code",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("courseSections.placeholder.code")}
          showCount
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceIdFilter
          value={modelFilter["subjectValue"]}
          onChange={handleChangeSelectFilter({
            fieldName: "subject",
            fieldType: "equal",
            classFilter: IdFilter,
          })}
          classFilter={SubjectFilter}
          getList={courseSectionRepository.filterListSubject}
          placeHolder={translate("courseSections.placeholder.subject")}
          label={translate("courseSections.subject")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceIdFilter
          value={modelFilter["appUserValue"]}
          onChange={(id, value) => {
            value.name = value?.displayName;
            handleChangeSelectFilter({
              fieldName: "appUser",
              fieldType: "equal",
              classFilter: IdFilter,
            })(id, value);
          }}
          classFilter={AppUserFilter}
          getList={courseSectionRepository.filterListAppUser}
          placeHolder={translate("courseSections.placeholder.appUser")}
          label={translate("courseSections.appUser")}
          render={(appUser: AppUser) => appUser?.displayName}
          searchProperty="search"
          searchType=""
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceIdFilter
          value={modelFilter["semesterValue"]}
          onChange={handleChangeSelectFilter({
            fieldName: "semester",
            fieldType: "equal",
            classFilter: IdFilter,
          })}
          classFilter={SemesterFilter}
          getList={courseSectionRepository.filterListSemester}
          placeHolder={translate("courseSections.placeholder.semester")}
          label={translate("courseSections.semester")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceIdFilter
          value={modelFilter["classShiftValue"]}
          onChange={handleChangeSelectFilter({
            fieldName: "classShift",
            fieldType: "equal",
            classFilter: IdFilter,
          })}
          classFilter={ClassShiftFilter}
          getList={courseSectionRepository.filterListClassShift}
          placeHolder={translate("courseSections.placeholder.classShift")}
          label={translate("courseSections.classShift")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceDateFilter
          label="Ngày học"
          onChange={handleChangeDateFilter}
          value={modelFilter?.lessonDate?.greaterEqual as any}
          type={1}
          placeholder={"Chọn ngày học"}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceIdFilter
          value={modelFilter["educationTypeValue"]}
          onChange={handleChangeSelectFilter({
            fieldName: "educationType",
            fieldType: "equal",
            classFilter: IdFilter,
          })}
          classFilter={EducationTypeFilter}
          getList={courseSectionRepository.filterListEducationType}
          placeHolder={"Chọn hệ đào tạo"}
          label={"Hệ đào tạo"}
        />
      </div>

    </Drawer>
  );
}

export default CourseSectionAdvanceFilter;
